import React, { useEffect, useMemo, useState } from 'react';
import {
  KendoReactGrid,
  GridColumn,
  GridCustomFilterCellProps
} from '@Shared.Angular/kendo.react/KendoReactGrid';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { ApiDateTime } from '@Shared.Angular/@types/dateTime';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor
} from '@progress/kendo-data-query';
import ActionButtons from '../ActionButtons';
import DeleteModal from '@Client/runner.shared/modal/delete/DeleteModal';
import {
  DeleteEntityTypes,
  FlowinglyPermissions,
  FlowModelPublishType
} from '@Shared.Angular/flowingly.services/flowingly.constants';
import { LibraryTagsModal } from '../runner.library.tags/LibraryTagsModal';
import { ICopyProcedureResult } from '../runner.library.procedures/runner.library.procedures.copy.dialog.controller';
import { HeaderTdElement } from '@progress/kendo-react-data-tools';
import { GridCustomCellProps } from '@progress/kendo-react-grid';
import StatusBadge from '../StatusBadge';
import {
  IFlowModelSummary,
  IFlowModelTagSummary
} from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelSummary';
import IProcedureOverview from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureOverview';
import IProcedureTagSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureTagSummary';
import IFormThemeOverview from '@Shared.Angular/@types/core/contracts/queryModel/formTheme/formThemeOverview';
import { TagFilter } from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import { PublicType } from '@Shared.Angular/@types/core/contracts/constants/publicType';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import { DateFilter } from '@Shared.Angular/flowingly.components/Filters/DateFilter/DateFilter';
import { getFormThemeLastUpdatedDate } from '@Shared.Angular/flowingly.services/data/formThemeDateUtils';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import GridAutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/GridAutoCompleteFilter';

const LibraryDraftsGrid = () => {
  const APP_CONFIG = getService('APP_CONFIG');
  const procedureApiService = getService('procedureApiService');
  const flowModelApiService = getService('flowModelApiService');
  const formThemesApiService = getService('formThemesApiService');
  const permissions = getService('permissionsService');
  const $state = getService('$state');
  const $q = getService('$q');
  const notificationService = getService('notificationService');
  const dialogService = getService('dialogService');
  const modelerUrlService = getService('tempModelerUrlService');
  const moment = getService('momentService');
  const pubsubService = getService('pubsubService');
  const categoryApiService = getService('categoryApiService');
  const libraryService = getService('libraryService');

  const pageSize = 15;
  const [allGridItems, setAllGridItems] = useState<IDraftGridItem[]>([]);
  const [gridItemsLoaded, setGridItemsLoaded] = useState(false);
  const [viewTagsTarget, setViewTagsTarget] = useState<IDraftGridItem>();
  const [usedTagIds, setUsedTagIds] = useState<Guid[]>([]);
  const [tagFilterIds, setTagFilterIds] = useState<Guid[]>([]);
  const [categoryFilterId, setCategoryFilterId] = React.useState<Guid | null>(
    null
  );
  const canCreateFlowModels = permissions.currentUserHasPermission(
    FlowinglyPermissions.FLOWMODEL_CREATE
  );
  const canCreateProcedures = permissions.currentUserHasPermission(
    FlowinglyPermissions.PROCEDURE_CREATE
  );
  const canCreateFormThemes = permissions.currentUserHasPermission(
    FlowinglyPermissions.FORM_THEME_CREATE
  );
  const canDeleteFormThemes = permissions.currentUserHasPermission(
    FlowinglyPermissions.FORM_THEME_DELETE
  );
  const [formThemeDeleteTarget, setFormThemeDeleteTarget] =
    useState<IDraftGridItem | null>(null);
  const [isDeletingFormTheme, setIsDeletingFormTheme] = useState(false);

  const subscriberId = 'libraryDraftsGrid';
  pubsubService.subscribeManyLate(
    [
      'PROCEDURE_CREATED',
      'PROCEDURE_UPDATED',
      'PROCEDURE_PUBLISHED',
      'PROCEDURE_DELETED',
      'SIGNALR_WORKFLOW_PUBLISHED',
      'SIGNALR_WORKFLOW_UNPUBLISHED',
      'SIGNALR_NEW_FLOW_MODEL_SAVED',
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      'SIGNALR_SETUP_FLOW_MODEL_DELETED',
      'SIGNALR_WORKFLOW_UNPUBLISHED_CHANGES'
    ],
    () => {
      getData();
    },
    subscriberId
  );

  const getData = () => {
    const promises = {
      flowModels: flowModelApiService.deferedFlowModels.promise,
      flowModelTags: flowModelApiService.deferedFlowModelTags.promise,
      procedures: APP_CONFIG.enableProcedures
        ? procedureApiService.deferedProcedures.promise
        : $q.resolve(undefined),
      procedureTags: APP_CONFIG.enableProcedures
        ? procedureApiService.deferedProcedureTags.promise
        : $q.resolve(undefined),
      formThemes: APP_CONFIG.allowWorkflow
        ? formThemesApiService.deferredFormThemes.promise
        : $q.resolve(undefined)
    };

    $q.all([
      promises.flowModels,
      promises.flowModelTags,
      promises.procedures,
      promises.procedureTags,
      promises.formThemes
    ]).then(
      ([flowModels, flowModelTags, procedures, procedureTags, formThemes]: [
        IFlowModelSummary[],
        IFlowModelTagSummary[],
        IProcedureOverview[] | undefined,
        IProcedureTagSummary[] | undefined,
        IFormThemeOverview[] | undefined
      ]) => {
        const usedTagIdSet = new Set<Guid>();
        const flowModelTagsMap = flowModelTags.reduce((acc, tagSummary) => {
          acc[tagSummary.flowModelId] = tagSummary.tags;
          return acc;
        }, {} as Record<string, Guid[]>);

        const allDrafts = flowModels
          .filter((fm) => fm.publishTypeValue === null)
          .map(
            (fm) =>
              ({
                id: fm.id,
                name: fm.name,
                lastUpdated: fm.lastEditedDate,
                lastUpdatedLocal: new Date(fm.lastEditedDate + 'Z'),
                createdBy: fm.creatorName,
                editable: fm.editableByCurrentUser,
                type:
                  fm.flowModelType === FlowModelPublishType.WORKFLOW
                    ? 'Workflow'
                    : fm.flowModelType === FlowModelPublishType.PROCESS_MAP
                    ? 'Process Map'
                    : 'Flow Model',
                publicType: fm.publicType,
                status: fm.status,
                categoryId: fm.categoryId,
                categoryName: fm.category,
                tagIds: flowModelTagsMap[fm.id] || []
              } as IDraftGridItem)
          );

        if (procedures && procedureTags) {
          const procedureTagsMap = procedureTags.reduce((acc, tagSummary) => {
            acc[tagSummary.procedureId] = tagSummary.tagIds;
            return acc;
          }, {} as Record<string, Guid[]>);

          allDrafts.push(
            ...procedures
              .filter((p) => !p.published)
              .map(
                (p) =>
                  ({
                    id: p.id,
                    name: p.name,
                    lastUpdated: p.updatedDate,
                    lastUpdatedLocal: new Date(p.updatedDate + 'Z'),
                    createdBy: p.creatorName,
                    editable: p.userCanEdit,
                    publishable: p.userCanPublish,
                    userCanDelete: p.userCanDelete,
                    type: 'SOP',
                    publicType: p.publicType,
                    status: 'Draft',
                    categoryId: p.categoryId,
                    categoryName: p.categoryName,
                    tagIds: procedureTagsMap[p.id] || []
                  } as IDraftGridItem)
              )
          );
        }

        const formThemeList = formThemes ?? [];

        if (formThemeList.length > 0) {
          const draftFormThemes = formThemeList
            .filter((theme) => {
              const normalizedStatus = theme.status?.toLowerCase();
              if (normalizedStatus) {
                return normalizedStatus === 'draft';
              }
              return false;
            })
            .map((theme) => {
              const lastUpdatedLocal = getFormThemeLastUpdatedDate(theme);
              return {
                id: theme.id,
                name: theme.name,
                lastUpdated: theme.lastUpdatedUtc ?? null,
                lastUpdatedLocal,
                createdBy: theme.createdBy,
                editable: canCreateFormThemes,
                publishable: true,
                type: 'Form Theme' as const,
                publicType: null,
                status: theme.status || 'Draft',
                tagIds: [] as Guid[],
                categoryId: null,
                categoryName: null
              } satisfies IDraftGridItem;
            });

          allDrafts.push(...draftFormThemes);
        }

        allDrafts.forEach((d) => {
          d.tagIds.forEach((tagId) => usedTagIdSet.add(tagId));
        });
        setUsedTagIds(Array.from(usedTagIdSet));

        setAllGridItems(allDrafts);
        setGridItemsLoaded(true);
      }
    );
  };
  useEffect(() => {
    getData();
  }, []);

  const getTagFilter = () => {
    return {
      field: 'tagIds',
      operator: (itemTagIds: Guid[]) => {
        return (
          !tagFilterIds ||
          tagFilterIds.length === 0 ||
          (itemTagIds &&
            tagFilterIds.some((tagId) => itemTagIds.includes(tagId)))
        );
      }
    };
  };

  const getCategoryFilter = () => {
    return {
      field: 'categoryId',
      operator: (itemCategoryId: Guid) => {
        return !categoryFilterId || categoryFilterId === itemCategoryId;
      }
    };
  };

  const [filter, setFilter] = React.useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [getTagFilter()]
  });

  useEffect(() => {
    const newCategoryFilter = filter.filters.find(
      (f: FilterDescriptor) => f.field === 'categoryName'
    ) as FilterDescriptor;
    const validFilters = newCategoryFilter
      ? filter.filters.filter(
          (f: FilterDescriptor) =>
            f.field !== 'categoryId' &&
            (categoryFilterId || f.field !== 'categoryName')
        )
      : filter.filters;
    setFilter({
      ...filter,
      filters: validFilters.map((f: FilterDescriptor) => {
        if (f.field === 'tagIds') {
          return getTagFilter();
        } else if (f.field === 'categoryName') {
          return getCategoryFilter();
        } else {
          return f;
        }
      })
    });
  }, [tagFilterIds, categoryFilterId]);

  const namesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.name))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const nameFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, namesMemo, 'Search by Name'),
    [namesMemo]
  );

  const creatorNamesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.createdBy))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const creatorFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, creatorNamesMemo, 'Search by Creator'),
    [creatorNamesMemo]
  );

  const typesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.type))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const typesFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, typesMemo, 'Search by Type'),
    [typesMemo]
  );

  const categoryFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <HeaderTdElement
        columnId={props.thProps?.columnId || ''}
        {...props.thProps}
      >
        <KendoReactDropDownTree
          categories={categoryApiService.cachedCategories}
          categoryClicked={(id: Guid) => {
            props.onChange({
              field: 'categoryId',
              operator: 'eq',
              value: id
            } as any);
            setCategoryFilterId(id);
          }}
          selectedId={categoryFilterId}
          dataItemKey="id"
          textField="name"
          subItemsField="subCategories"
          defaultItemId={null}
          showFilter={true}
        />
      </HeaderTdElement>
    );
  };

  const dateFilterCell: React.FC<GridCustomFilterCellProps> = (props) => (
    <HeaderTdElement
      columnId={props.thProps?.columnId || ''}
      {...props.thProps}
    >
      <DateFilter
        value={props.value as Date | null}
        onChange={(args) =>
          props.onChange?.({
            value: args.value,
            operator: args.operator,
            syntheticEvent: args.syntheticEvent
          })
        }
      />
    </HeaderTdElement>
  );

  const statusMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.status))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const statusFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, statusMemo, 'Search by Status'),
    [statusMemo]
  );

  const initialSort = useMemo(
    () => [{ field: 'lastUpdatedLocal', dir: 'desc' } as SortDescriptor],
    []
  );

  const ellipsisCell = (props: GridCustomCellProps) => {
    const value = props.dataItem[props.field];
    return (
      <td {...props.tdProps} title={value}>
        {value}
      </td>
    );
  };

  const dateCell = (props: GridCustomCellProps) => {
    const value = props.dataItem[props.field];
    if (!value) {
      return <td {...props.tdProps}></td>;
    }
    return (
      <td
        {...props.tdProps}
        title={moment(value).format('YYYY-MM-DD HH:mm:ss')}
      >
        {moment(value).format('DD/MM/YYYY')}
      </td>
    );
  };

  const statusCell = (props: GridCustomCellProps) => {
    return (
      <td {...props.tdProps}>
        <StatusBadge status={props.dataItem.status} />
      </td>
    );
  };

  const openLockDialog = (dataItem: IDraftGridItem) => {
    dialogService
      .showDialog({
        template:
          'Client/runner.library/runner.library.flow.model.owner/runner.library.flow.model.owner.dialog.tmpl.html',
        controller: 'flowModelOwnerDialogController',
        appendClassName: 'ngdialog-theme-plain w-700',
        data: {
          flowModelId: dataItem.id,
          flowModelName: dataItem.name,
          isPublic: dataItem.publicType !== null,
          publicType: dataItem.publicType,
          isProcessMapType: false
        }
      })
      .then((data) => {
        if (dialogService.isCloseModalWithCancelAction(data)) {
          return;
        }

        if (data != null) {
          if (data.message)
            notificationService.showSuccessToast(data.message, 4000);
          else
            notificationService.showSuccessToast(
              `${dataItem.name} updated`,
              4000
            );
        }
      });
  };

  const goToEditor = (dataItem: IDraftGridItem) => {
    if (dataItem.type === 'SOP') {
      $state.go(
        STATE_MAPPINGS.procedure,
        { procedureId: dataItem.id, viewMode: ProcedureViewMode.Edit },
        {}
      );
    } else {
      modelerUrlService.openModeler({ flowModelId: dataItem.id });
    }
  };

  const createCopyOfItem = (dataItem: IDraftGridItem) => {
    if (dataItem.type === 'SOP') {
      dialogService
        .showDialog({
          template:
            'Client/runner.library/runner.library.procedures/runner.library.procedures.copy.dialog.tmpl.html',
          controller: 'procedureCopyDialogController',
          appendClassName: 'ngdialog-normal',
          data: {
            existingProcedureId: dataItem.id,
            existingProcedureName: dataItem.name
          }
        })
        .then((result: ICopyProcedureResult) => {
          if (dialogService.isCloseModalWithCancelAction(result)) {
            return;
          }

          $state.go(STATE_MAPPINGS.procedure, {
            procedureId: result.id,
            viewMode: ProcedureViewMode.ReadOnly
          });
        });
    } else {
      const cloneFlowModel = true;
      modelerUrlService.openModeler({
        flowModelId: dataItem.id,
        cloneModel: cloneFlowModel
      });
    }
  };

  const openFormThemeEditor = (dataItem: IDraftGridItem) => {
    $state.go(STATE_MAPPINGS.libraryFormTheme, {
      formThemeId: dataItem.id
    });
  };

  const copyFormTheme = (dataItem: IDraftGridItem) => {
    const copyUrl = `/library/formthemes/${dataItem.id}?copy=true`;
    modelerUrlService.openLinkInNewTab(copyUrl);
  };

  const openFormThemeDeleteDialog = (dataItem: IDraftGridItem) => {
    setFormThemeDeleteTarget(dataItem);
    setIsDeletingFormTheme(false);
  };

  const closeFormThemeDeleteDialog = () => {
    setFormThemeDeleteTarget(null);
    setIsDeletingFormTheme(false);
  };

  const handleFormThemeDelete = () => {
    if (!formThemeDeleteTarget || isDeletingFormTheme) {
      return;
    }

    setIsDeletingFormTheme(true);
    formThemesApiService
      .deleteFormTheme(formThemeDeleteTarget.id)
      .then(() => {
        notificationService.showSuccessToast(
          `${formThemeDeleteTarget.name} deleted.`
        );
        setAllGridItems((items) =>
          items.filter((item) => item.id !== formThemeDeleteTarget.id)
        );
      })
      .catch(() => {
        notificationService.showErrorToast(
          'Failed to delete form theme. Please try again.'
        );
      })
      .finally(() => {
        setIsDeletingFormTheme(false);
        setFormThemeDeleteTarget(null);
      });
  };

  const openDeleteDialog = (dataItem: IDraftGridItem) => {
    if (dataItem.type === 'SOP') {
      showProcedureDeleteDialog(dataItem);
    } else if (dataItem.type === 'Form Theme') {
      openFormThemeDeleteDialog(dataItem);
    } else {
      showFlowModelDeleteDialog(dataItem);
    }
  };

  const showProcedureDeleteDialog = (dataItem: IDraftGridItem) => {
    dialogService
      .showDialog({
        template:
          'Client/runner.setup/runner.setup.delete/runner.setup.delete.dialog.tmpl.html',
        controller: 'setupDeleteDialogController',
        appendClassName: 'ngdialog-normal',
        data: {
          itemToDelete: dataItem,
          entityType: DeleteEntityTypes.procedure
        }
      })
      .then((result) => {
        if (dialogService.isCloseModalWithCancelAction(result)) {
          return;
        }
        procedureApiService
          .deleteProcedure(dataItem.id)
          .then(() => {
            notificationService.showSuccessToast('SOP successfully Deleted.');
            setAllGridItems((items) =>
              items.filter((item) => item.id !== dataItem.id)
            );
          })
          .catch((error: { status?: number }) =>
            notificationService.showErrorToast(
              error?.status === 403
                ? 'You do not have permission to delete this SOP'
                : 'Failed to delete SOP'
            )
          );
      });
  };

  const showFlowModelDeleteDialog = (dataItem: IDraftGridItem) => {
    dialogService
      .showDialog({
        template:
          'Client/runner.setup/runner.setup.delete/runner.setup.delete.dialog.tmpl.html',
        controller: 'setupDeleteDialogController',
        appendClassName: 'ngdialog-normal',
        data: {
          itemToDelete: dataItem,
          entityType: DeleteEntityTypes.flowModelEntityType
        }
      })
      .then(function (response) {
        if (dialogService.isCloseModalWithCancelAction(response)) {
          return;
        }
        flowModelApiService.deleteFlowModel(dataItem.id).then(() => {
          notificationService.showSuccessToast('Flow Model Deleted');
        });
      });
  };

  const generateActionsCell = (props: GridCustomCellProps) => {
    const item: IDraftGridItem = props.dataItem;
    let flowModelLockHandler: () => void;
    let editHandler: () => void;
    let copyHandler: () => void;
    const tagHandler =
      APP_CONFIG.enableTagging && item.type !== 'Form Theme'
        ? () => setViewTagsTarget(item)
        : undefined;
    let deleteHandler: () => void;

    if (item.type === 'SOP') {
      if (item.editable || item.publishable) {
        editHandler = () => goToEditor(item);
      }
      if (canCreateProcedures) {
        copyHandler = () => createCopyOfItem(item);
      }
      if (item.userCanDelete) {
        deleteHandler = () => openDeleteDialog(item);
      }
    } else if (item.type === 'Form Theme') {
      if (canCreateFormThemes) {
        editHandler = () => openFormThemeEditor(item);
        copyHandler = () => copyFormTheme(item);
      }
      if (canDeleteFormThemes) {
        deleteHandler = () => openFormThemeDeleteDialog(item);
      }
    } else {
      if (item.editable) {
        flowModelLockHandler = () => openLockDialog(item);
        editHandler = () => goToEditor(item);
        deleteHandler = () => openDeleteDialog(item);
      }
      if (item.editable && canCreateFlowModels) {
        copyHandler = () => createCopyOfItem(item);
      }
    }
    return (
      <td {...props.tdProps}>
        <ActionButtons
          showButtons={{
            procedureLock: item.type === 'SOP',
            flowModelLock: item.type !== 'SOP' && item.type !== 'Form Theme',
            edit: true,
            copy: true,
            tags: APP_CONFIG.enableTagging,
            delete: true
          }}
          onFlowModelLockClick={flowModelLockHandler}
          onEditClick={editHandler}
          onCopyClick={copyHandler}
          onTagsClick={tagHandler}
          onDeleteClick={deleteHandler}
        />
      </td>
    );
  };

  return (
    (!gridItemsLoaded && <div></div>) ||
    (gridItemsLoaded && allGridItems.length > 0 && (
      <div className="library-grid">
        <TagFilter usedTagIds={usedTagIds} onApply={setTagFilterIds} />
        <KendoReactGrid
          id="library-drafts-grid"
          autoProcessData={{
            filter: true,
            search: true,
            sort: true,
            group: false,
            page: true
          }}
          data={allGridItems}
          dataItemKey="id"
          defaultSort={initialSort}
          defaultTake={pageSize}
          filter={filter}
          filterable={true}
          onFilterChange={(filterChangeEvent) => {
            setFilter(filterChangeEvent.filter);
          }}
          pageable={{
            buttonCount: 10,
            info: true,
            previousNext: true
          }}
          sortable={true}
          resizable={true}
        >
          <GridColumn
            field="name"
            title="Name"
            className="column-name ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: nameFilterCellMemo }}
          />
          <GridColumn
            field="createdBy"
            title="Created By"
            className="column-created-by ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: creatorFilterCellMemo }}
          />
          <GridColumn
            field="lastUpdatedLocal"
            title="Last Updated"
            filter="date"
            className="column-last-updated"
            cells={{ data: dateCell, filterCell: dateFilterCell }}
          />
          <GridColumn
            field="type"
            title="Type"
            className="column-type"
            cells={{ filterCell: typesFilterCellMemo }}
          />
          <GridColumn
            field="categoryName"
            title="Category"
            className="column-category ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: categoryFilterCell }}
          />
          {APP_CONFIG.enableGovernance && (
            <GridColumn
              field="status"
              title="Status"
              className="column-status"
              cells={{
                data: statusCell,
                filterCell: statusFilterCellMemo
              }}
            />
          )}
          <GridColumn
            title="Actions"
            sortable={false}
            filterable={false}
            resizable={false}
            width={250}
            className="column-actions"
            cells={{
              data: generateActionsCell
            }}
          />
        </KendoReactGrid>
        {viewTagsTarget && (
          <LibraryTagsModal
            title={viewTagsTarget.name}
            onClose={() => setViewTagsTarget(undefined)}
            procedureId={
              viewTagsTarget.type === 'SOP' ? viewTagsTarget.id : undefined
            }
            flowmodelId={
              viewTagsTarget.type !== 'SOP' ? viewTagsTarget.id : undefined
            }
          />
        )}
        {formThemeDeleteTarget && (
          <DeleteModal
            id="form-theme-draft-delete-modal"
            title="Delete Form Theme"
            subject={formThemeDeleteTarget.name}
            closeModal={closeFormThemeDeleteDialog}
            deleteAction={
              !isDeletingFormTheme ? handleFormThemeDelete : undefined
            }
          />
        )}
      </div>
    )) ||
    (gridItemsLoaded && allGridItems.length === 0 && (
      <EmptyState
        summaryText="Build your process Library"
        bodyText={libraryService.emptyStateBody}
        buttonText={canCreateFlowModels ? 'Add a Flow Model' : undefined}
      />
    ))
  );
};

export default LibraryDraftsGrid;

export interface IDraftGridItem {
  id: Guid;
  name: string;
  lastUpdated: ApiDateTime;
  lastUpdatedLocal: Date;
  createdBy: string;
  editable: boolean;
  publishable: boolean;
  userCanDelete?: boolean;
  type: 'SOP' | 'Flow Model' | 'Workflow' | 'Process Map' | 'Form Theme';
  publicType: PublicType | null;
  status: string;
  tagIds?: Guid[];
  categoryId: Guid;
  categoryName: string;
}
